import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellIcon, LogOutIcon } from 'lucide-react'
import { PERSONA_LABELS } from '@/shared/domain'
import { useAuth, useCurrentPersona } from '@/shared/ui/hooks'
import { deriveInitials } from '@/shared/ui/components/derive-initials'
import { UnitsToggle } from './units-toggle'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/primitives'

export function HeaderMenu() {
  const { signOut, user } = useAuth()
  const { persona } = useCurrentPersona()
  const navigate = useNavigate()
  const [isSigningOut, setIsSigningOut] = useState(false)

  const initials = deriveInitials(user?.fullName, user?.email)
  const displayName = user?.fullName?.trim() || user?.email || 'Signed in'
  const roleLabel = PERSONA_LABELS[persona]

  async function handleSignOut() {
    setIsSigningOut(true)
    try {
      await signOut()
      navigate('/sign-in', { replace: true })
    } finally {
      setIsSigningOut(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex shrink-0 items-center justify-center rounded-full outline-none focus-visible:outline-none"
          aria-label="User menu"
        >
          <Avatar className="size-11 rounded-full border border-primary/25 bg-blaze-soft dark:border-transparent dark:bg-accent/60">
            {user?.avatarUrl && (
              <AvatarImage
                src={user.avatarUrl}
                alt={user.fullName ?? user.email ?? ''}
                className="object-cover"
              />
            )}
            <AvatarFallback className="rounded-full bg-transparent text-sm font-semibold text-primary dark:text-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5 px-2 py-2">
          <span className="truncate text-sm font-medium text-foreground">{displayName}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">{roleLabel}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <UnitsToggle />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            navigate('/account/notifications')
          }}
        >
          <BellIcon className="size-4" />
          Notifications
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isSigningOut}
          onSelect={() => {
            void handleSignOut()
          }}
        >
          <LogOutIcon className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
